// pipe (ask-PIPE): the ONE fetch-based Server-Sent-Events reader. The app's existing SSE plumbing
// (`modules/cloud/services/eventsource.ts`, `blocksuite/ai/provider/event-source.ts`) rides the
// browser `EventSource`, which is GET-only — `POST /api/ask?stream=true` carries a JSON body and the
// cookie session through the shared `request()` core, so its `text/event-stream` body is parsed here
// incrementally off the fetch `ReadableStream`. Dependency-free and pure (the parser is a plain
// push/flush state machine) so it is unit-testable without a stream and safe from any seam.
//
// The wire subset honoured (WHATWG EventSource framing): `event:` / `data:` fields, `\n` or `\r\n`
// line ends, a blank line dispatches, multiple `data:` lines join with `\n`, `:` comment lines and
// `id:`/`retry:` are ignored, an event with no `event:` field is `message`.

export interface SseEvent {
  event: string;
  data: string;
}

/** Incremental SSE frame parser: feed text chunks, collect the events each chunk completes. */
export class SseParser {
  private buffer = '';
  private event = 'message';
  private data: string[] = [];

  /** Push a decoded chunk; returns every event completed by it (possibly none). */
  push(chunk: string): SseEvent[] {
    this.buffer += chunk;
    const out: SseEvent[] = [];
    let nl: number;
    while ((nl = this.buffer.indexOf('\n')) !== -1) {
      let line = this.buffer.slice(0, nl);
      this.buffer = this.buffer.slice(nl + 1);
      if (line.endsWith('\r')) line = line.slice(0, -1);
      const ev = this.line(line);
      if (ev) out.push(ev);
    }
    return out;
  }

  /** End of stream: a trailing frame without its blank line still dispatches. */
  end(): SseEvent[] {
    const out: SseEvent[] = [];
    if (this.buffer.length > 0) {
      const ev = this.line(this.buffer.replace(/\r$/, ''));
      this.buffer = '';
      if (ev) out.push(ev);
    }
    const last = this.dispatch();
    if (last) out.push(last);
    return out;
  }

  private line(line: string): SseEvent | undefined {
    if (line === '') return this.dispatch();
    if (line.startsWith(':')) return undefined;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') this.event = value;
    else if (field === 'data') this.data.push(value);
    return undefined;
  }

  private dispatch(): SseEvent | undefined {
    if (this.data.length === 0) {
      // A frame with no data is dropped (spec) — but its event name must not leak into the next.
      this.event = 'message';
      return undefined;
    }
    const ev = { event: this.event, data: this.data.join('\n') };
    this.event = 'message';
    this.data = [];
    return ev;
  }
}

/** Iterate the events of a fetch response body until it closes. Cancels the reader on early exit
 *  (a consumer `return`ing after the terminal event releases the connection). */
export async function* readSseEvents(
  body: ReadableStream<Uint8Array>
): AsyncGenerator<SseEvent, void, undefined> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = new SseParser();
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      for (const ev of parser.push(decoder.decode(value, { stream: true }))) {
        yield ev;
      }
    }
    for (const ev of parser.push(decoder.decode())) yield ev;
    for (const ev of parser.end()) yield ev;
  } finally {
    reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
